import React, { ReactNode } from "react";

import { Box } from "@UI/Components/Box/Box";
import { Button } from "@UI/Components/Button/Button";
import { Flex } from "@UI/Components/Flex/Flex";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import useI18n from "@I18n/hooks/useI18n";

import { AuthScreenLayout } from "../../Components/AuthScreenLayout/AuthScreenLayout";
import { ServiceProviderMap } from "../../Components/ServiceProviderMap/ServiceProviderMap";
import { TaskCreationLayout } from "../../Components/TaskCreationLayout/TaskCreationLayout";
import { useTaskCreationFlow } from "../../Contexts/TaskCreationFlow";
import { useFlowBack } from "../../hooks/useFlowBack";
import { useCurrentStepName } from "../../hooks/useTaskCreationSummary";
import messages from "../../messages";
import { getServiceProviderPins } from "../../utils/serviceProviderPins";

const ARTWORK_WIDTH = "509px";
const ARTWORK_HEIGHT = "542px";
const SECTION_WIDTH = "650px";
const COLUMN_GAP = "48px";
const CONTENT_GUTTER = "126px";
const CONTENT_WIDTH = "1440px";

export interface IAuthStepLayoutProps {
  title: ReactNode;
  /** A sales line on the email screen, the address on the login screen. */
  subtitle?: ReactNode;
  children?: ReactNode;
}

/**
 * The chrome all three auth screens share, wired to the flow, so each screen
 * only says what its heading is. This step has no summary panel
 * (`TaskCreationShell` drops it), so the Back button lives here.
 */
export const AuthStepLayout = ({
  title,
  subtitle,
  children,
}: IAuthStepLayoutProps) => {
  const {
    answeredQuestions,
    totalQuestions,
    currentStepNumber,
    totalSteps,
    isFlowLoading,
    config,
  } = useTaskCreationFlow();
  const stepName = useCurrentStepName();
  const handleBack = useFlowBack();
  const { formatMessage } = useI18n();
  const serviceProviderPins = getServiceProviderPins(
    config?.category.s_category_id,
    formatMessage
  );

  return (
    <Flex
      direction={{ base: "column", lg: "row" }}
      alignItems={{ base: "stretch", lg: "flex-start" }}
      justifyContent="space-between"
      gap={{ base: "0", lg: COLUMN_GAP }}
      maxWidth={{ base: "none", lg: CONTENT_WIDTH }}
      paddingLeft={{ base: "16px", lg: CONTENT_GUTTER }}
      paddingRight={{ base: "16px", lg: "0" }}
      paddingTop={{ base: "0", lg: "32px" }}
      paddingBottom={{ base: "0", lg: "80px" }}
    >
      <Box
        flex={{ base: "1", lg: `0 1 ${SECTION_WIDTH}` }}
        minWidth={0}
        maxWidth={{ base: "none", lg: SECTION_WIDTH }}
      >
        <TaskCreationLayout
          answeredQuestions={answeredQuestions}
          totalQuestions={totalQuestions}
          step={currentStepNumber}
          totalSteps={totalSteps}
          stepName={stepName}
          isLoading={isFlowLoading}
        >
          <AuthScreenLayout
            title={title}
            subtitle={subtitle}
            onBack={handleBack}
          >
            {children}
          </AuthScreenLayout>
          <Flex
            display={{ base: "none", lg: "flex" }}
            justifyContent="flex-end"
            paddingTop="32px"
          >
            <Button
              onClick={handleBack}
              background="gray.300"
              color="gray.800"
              height="48px"
              borderRadius="10px"
              paddingX="22px"
              fontSize="md"
            >
              <FormattedMessage {...messages["taskCreation.cta.back"]} />
            </Button>
          </Flex>
        </TaskCreationLayout>
      </Box>
      <Box
        display={{ base: "none", lg: "block" }}
        flex={`0 0 ${ARTWORK_WIDTH}`}
        height={ARTWORK_HEIGHT}
        aria-hidden
      >
        <ServiceProviderMap variant="desktop" pins={serviceProviderPins} />
      </Box>
    </Flex>
  );
};
